import { apiClient } from '@lib/core/api';

/**
 * Deterministic social-media publish.
 *
 * Calls chatbot-api `POST /integration/social/publish`, which forwards to
 * yepairag `/yepairag/social/publish` and runs the Composio write directly
 * (e.g. FACEBOOK_CREATE_PHOTO_POST) — WITHOUT routing through the agent's
 * search→plan→execute tool loop. This is the reliable counterpart to asking
 * Maya to publish in chat: an approved draft's content/image/channel are fixed,
 * so publishing is executed in code instead of gambling on the model emitting
 * the right tool call (which can fumble params or never fire, leaving "nothing
 * published, no error").
 *
 * Platform support is decided by directPublish's channel map (and server-side by
 * yepairag's _SUPPORTED set); the VITE_DETERMINISTIC_SOCIAL_PUBLISH dark-launch
 * flag is gone — button publishes are deterministic, full stop (Monica, 2026-08-10).
 */

export interface SocialPublishResult {
  ok: boolean;
  platform: string;
  page_id?: string;
  /** Published post id (e.g. Facebook composite PageID_PostID). */
  post_id?: string;
  /** Public URL of the published post, for the confirmation card link. */
  post_url?: string;
  result?: unknown;
}

export interface SocialPublishParams {
  /** Tenant id — equals the logged-in user's id. */
  tenantId: string;
  /** Target platform, e.g. "facebook". */
  platform: string;
  /** Post text / caption (the reviewed draft body). */
  message: string;
  /** Publicly retrievable image URL for a photo post. */
  imageUrl?: string;
  /** Target Page id; resolved from the connection's managed pages when omitted. */
  pageId?: string;
  /**
   * The draft's title and id, so the backend can archive this publish to the Material Hub
   * itself. Both come from the approved draft in hand — not from the model — so the archived
   * item links to the right draft (and inherits its body and cover) with nothing to restate.
   */
  title?: string;
  draftId?: string;
  /**
   * Publish again even though this piece is already live on the channel (contract
   * CONTRACT-publish-ops v1.2 §5.4). Without it the server answers `ALREADY_PUBLISHED:`.
   * Only the Published page's Republish sends it.
   */
  republish?: boolean;
}

export async function publishSocialDirect(
  params: SocialPublishParams
): Promise<SocialPublishResult> {
  const { data } = await apiClient.chatbotPost<SocialPublishResult>(
    '/integration/social/publish',
    {
      tenant_id: params.tenantId,
      platform: params.platform,
      message: params.message,
      image_url: params.imageUrl,
      page_id: params.pageId,
      title: params.title,
      draft_id: params.draftId,
      ...(params.republish ? { republish: true } : {}),
    }
  );
  return data;
}

// Platforms with a deterministic execution path server-side. Others fall back to
// the agent publish path.
